export enum WellKnownRoles {
	USER = "user",
	REP = "rep",
	WAREHOUSE = "warehouse",
	SHIPPER = "shipper",
	ADMIN = "admin",
	SUPER_ADMIN = "super_admin",
}

export type User = {
	username: string;
	firstName: string;
	lastName: string;
	initials?: string;
	userId: number;
	roles: Role[];
	permissions: Permission[];
	claims: object;
	isAdmin: boolean;
};

export type Role = {
	name: string;
	permissions: Permission[];
};

export type Permission = {
	name: string;
};

export function initUser(user: User): User {
	const initials = `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
	return { ...user, initials };
}

export function hasRole(user: User, roleToCheck: WellKnownRoles | string): boolean {
	if (!process.env.VUE_APP_SECURITY_ENABLED) {
		return true;
	}

	const hasRole = user.roles.some((role) => role.name === roleToCheck);
	return hasRole;
}

export function hasAnyRole(user: User, rolesToCheck: (WellKnownRoles | string)[]): boolean {
	if (!process.env.VUE_APP_SECURITY_ENABLED) {
		return true;
	}

	return rolesToCheck.some((roleToCheck) => hasRole(user, roleToCheck));
}

export function hasPermission(user: User, permissionToCheck: string): boolean {
	if (!process.env.VUE_APP_SECURITY_ENABLED) {
		return true;
	}
	// Check both user's individual permissions and their roles' permissions
	return (
		user.permissions.some((permission) => permission.name === permissionToCheck) ||
		user.roles.some((role) =>
			role.permissions.some((permission) => permission.name === permissionToCheck)
		)
	);
}

export function hasAnyPermission(user: User, permissionsToCheck: string[]): boolean {
	if (!process.env.VUE_APP_SECURITY_ENABLED) {
		return true;
	}
	// Check if any of the permissions to check is present in the user's permissions or any of their roles' permissions
	return permissionsToCheck.some((permissionToCheck) => hasPermission(user, permissionToCheck));
}

/**
 * Permissions that indicate a user performs activities requiring the
 * Universal Hardware Bridge (printers, scales, scanners via UHB WebSocket).
 *
 * NOTE: PrintInvoice and PrintOrder are intentionally excluded.
 * Those use server-managed print jobs via /api/print/document
 * (PagedJSService + ezeep), not UHB hardware printing.
 */
export const HARDWARE_REQUIRED_PERMISSIONS: string[] = [
	// Shipping API permissions (creating shipments/labels)
	"Shipping_Shipments_Create",
	"Shipping_Labels_Create",
	// Shipper role permissions (label management)
	"Shipper_Shipping_CreateLabel",
	"Shipper_Shipping_VoidLabel",
	"Shipper_Shipping_CreateReturnLabel",
	// Printing permissions (hardware-based printing only)
	"SalesOrders_OrderStatus_PrintPickTicket",
	"SalesOrders_OrderStatus_PrintVendorPOS",
	"SalesOrders_OrderStatus_PrintPackingSlips",
	"Receiving_Commits_PrintPickTicket",
	"Purchasing_PurchaseOrders_Print",
	// Settings that involve hardware devices
	"Settings_Printers_ALL",
	"Settings_PackageTypes_ALL",
];

/**
 * Determines if the hardware integration (UHB WebSocket, scales, printers,
 * scanners, keyboard wedge scanner) should be activated for this user.
 *
 * Returns true for:
 * - Admin / Super Admin (always)
 * - Users with the Shipper role (role-based hardware access)
 * - Users with any permission that involves printing, shipping labels,
 *   or hardware device configuration
 */
export function shouldEnableHardware(user: User): boolean {
	if (!user) return false;

	// Admins always get hardware access
	if (hasAnyRole(user, [WellKnownRoles.ADMIN, WellKnownRoles.SUPER_ADMIN])) {
		return true;
	}

	// Shipper/Warehouse role always needs hardware (scales, printers, scanners)
	if (hasAnyRole(user, [WellKnownRoles.SHIPPER, WellKnownRoles.WAREHOUSE])) {
		return true;
	}

	// Check for any permission that requires hardware interaction
	return hasAnyPermission(user, HARDWARE_REQUIRED_PERMISSIONS);
}

export enum SystemPermissions {
	RegisterUser = "RegisterUser",
	SalesOrders_Quote_Search = "SalesOrders_Quote_Search",
	SalesOrders_Quote_View = "SalesOrders_Quote_View",
	SalesOrders_Quote_Create = "SalesOrders_Quote_Create",
	SalesOrders_Quote_Update = "SalesOrders_Quote_Update",
	SalesOrders_Quote_Cancel = "SalesOrders_Quote_Cancel",
	SalesOrders_Quote_PlaceOrder = "SalesOrders_Quote_PlaceOrder",
	SalesOrders_Quote_Notes_ALL = "SalesOrders_Quote_Notes_ALL",
	SalesOrders_Quote_Payments_ALL = "SalesOrders_Quote_Payments_ALL",
	SalesOrders_Order_Search = "SalesOrders_Order_Search",
	SalesOrders_Order_View = "SalesOrders_Order_View",
	SalesOrders_Order_Update = "SalesOrders_Order_Update",
	SalesOrders_Order_OverrideShippingAndHandling = "SalesOrders_Order_OverrideShippingAndHandling",
	SalesOrders_Order_Cancel = "SalesOrders_Order_Cancel",
	SalesOrders_Order_Process = "SalesOrders_Order_Process",
	SalesOrders_Order_Notes_ALL = "SalesOrders_Order_Notes_ALL",
	SalesOrders_Order_Payments_ALL = "SalesOrders_Order_Payments_ALL",
	SalesOrders_OrderStatus_PrintPickTicket = "SalesOrders_OrderStatus_PrintPickTicket",
	SalesOrders_OrderStatus_FillOrders = "SalesOrders_OrderStatus_FillOrders",
	SalesOrders_OrderStatus_PrintInvoice = "SalesOrders_OrderStatus_PrintInvoice",
	SalesOrders_OrderStatus_PrintOrder = "SalesOrders_OrderStatus_PrintOrder",
	SalesOrders_OrderStatus_PrintVendorPOS = "SalesOrders_OrderStatus_PrintVendorPOS",
	SalesOrders_OrderStatus_PrintPackingSlips = "SalesOrders_OrderStatus_PrintPackingSlips",
	SalesOrders_FindRelatedOrders_Search = "SalesOrders_FindRelatedOrders_Search",
	Purchasing_PurchaseOrders_Search = "Purchasing_PurchaseOrders_Search",
	Purchasing_PurchaseOrders_View = "Purchasing_PurchaseOrders_View",
	Purchasing_PurchaseOrders_Create = "Purchasing_PurchaseOrders_Create",
	Purchasing_PurchaseOrders_PlacePO = "Purchasing_PurchaseOrders_PlacePO",
	Purchasing_PurchaseOrders_Update_Unlock = "Purchasing_PurchaseOrders_Update_Unlock",
	Purchasing_PurchaseOrders_CancelPO = "Purchasing_PurchaseOrders_CancelPO",
	Purchasing_PurchaseOrder_Notes_ALL = "Purchasing_PurchaseOrder_Notes_ALL",
	Purchasing_PurchaseOrders_Print = "Purchasing_PurchaseOrders_Print",
	Purchasing_POsToGenerate_Search = "Purchasing_POsToGenerate_Search",
	Purchasing_POsToGenerate_PlaceStockPO = "Purchasing_POsToGenerate_PlaceStockPO",
	Purchasing_Supplier_Search_View = "Purchasing_Supplier_Search_View",
	Purchasing_Supplier_Update = "Purchasing_Supplier_Update",
	Purchasing_Supplier_Delete = "Purchasing_Supplier_Delete",
	Receiving_ReceivingEntry_Search_View = "Receiving_ReceivingEntry_Search_View",
	Receiving_ReceivingEntry_View = "Receiving_ReceivingEntry_View",
	Receiving_ReceivingEntry_Receive_UnReceive = "Receiving_ReceivingEntry_Receive_UnReceive",
	Receiving_Commits_Search_View = "Receiving_Commits_Search_View",
	Receiving_Commits_Commit_Decommit = "Receiving_Commits_Commit_Decommit",
	Receiving_Commits_PrintPickTicket = "Receiving_Commits_PrintPickTicket",
	Inventory_AvailableInventory_Search_View = "Inventory_AvailableInventory_Search_View",
	Inventory_InventoryItems_View_Search = "Inventory_InventoryItems_View_Search",
	Inventory_InventoryItems_Create_Update_Delete = "Inventory_InventoryItems_Create_Update_Delete",
	Inventory_BinUtility_View_Search = "Inventory_BinUtility_View_Search",
	Inventory_BinUtility_Update = "Inventory_BinUtility_Update",
	Inventory_SizeChart_View_Search = "Inventory_SizeChart_View_Search",
	Inventory_SizeChart_Create_Update_Delete = "Inventory_SizeChart_Create_Update_Delete",
	Inventory_ColorPalette_View_Search = "Inventory_ColorPalette_View_Search",
	Inventory_ColorPalette_Create_Update_Delete = "Inventory_ColorPalette_Create_Update_Delete",
	Inventory_PackageCategory_View_Search = "Inventory_PackageCategory_View_Search",
	Inventory_PackageCategory_Create_Update_Delete = "Inventory_PackageCategory_Create_Update_Delete",
	Inventory_SubItemCategory_View_Search = "Inventory_SubItemCategory_View_Search",
	Inventory_SubItemCategory_Create_Update_Delete = "Inventory_SubItemCategory_Create_Update_Delete",
	Inventory_PositionOfLetters_View = "Inventory_PositionOfLetters_View",
	Inventory_PositionOfLetters_Create_Update_Delete = "Inventory_PositionOfLetters_Create_Update_Delete",
	Inventory_ProcessTypes_View = "Inventory_ProcessTypes_View",
	Inventory_ProcessTypes_Create_Update_Delete = "Inventory_ProcessTypes_Create_Update_Delete",
	Relationships_Customer_ALL = "Relationships_Customer_ALL",
	Relationships_Vendor_ALL = "Relationships_Vendor_ALL",
	Relationships_Rep_ALL = "Relationships_Rep_ALL",
	Settings_CustomerCategory_ALL = "Settings_CustomerCategory_ALL",
	Settings_PaymentMethod_ALL = "Settings_PaymentMethod_ALL",
	Settings_PaymentTerms_ALL = "Settings_PaymentTerms_ALL",
	Settings_ShippingMethod_ALL = "Settings_ShippingMethod_ALL",
	Settings_Messages_ALL = "Settings_Messages_ALL",
	Settings_Security_ALL = "Settings_Security_ALL",
	Settings_PrintingLog_ALL = "Settings_PrintingLog_ALL",
	Settings_Printers_ALL = "Settings_Printers_ALL",
	Settings_PackageTypes_ALL = "Settings_PackageTypes_ALL",
	Settings_ZipTaxRate_ALL = "Settings_ZipTaxRate_ALL",
	Settings_SystemCategory_ALL = "Settings_SystemCategory_ALL",
	Reports_RepActivitySummary_ALL = "Reports_RepActivitySummary_ALL",
	Reports_SalesSummaryByCustomerOrder_ALL = "Reports_SalesSummaryByCustomerOrder_ALL",
	Reports_SalesDailySummaryByRep_ALL = "Reports_SalesDailySummaryByRep_ALL",
	Reports_SalesByStyleCode_ALL = "Reports_SalesByStyleCode_ALL",
	Reports_CustomerOpenOrdersLineItems_ALL = "Reports_CustomerOpenOrdersLineItems_ALL",
	Reports_QuoteStatus_ALL = "Reports_QuoteStatus_ALL",
	Reports_SamplesOverdue_ALL = "Reports_SamplesOverdue_ALL",
	Reports_OutstandingBalanceByRep_ALL = "Reports_OutstandingBalanceByRep_ALL",
	Reports_SalesTaxReport_ALL = "Reports_SalesTaxReport_ALL",
	Reports_SalesTaxByZipReport_ALL = "Reports_SalesTaxByZipReport_ALL",
	Reports_CustomerOrdersReport_ALL = "Reports_CustomerOrdersReport_ALL",
	Reports_QuoteNeedsReport_ALL = "Reports_QuoteNeedsReport_ALL",
	Reports_RepActivityComparison_ALL = "Reports_RepActivityComparison_ALL",
	Reports_OpenPOReport_ALL = "Reports_OpenPOReport_ALL",
	Reports_QuoteOrderCrossRef_ALL = "Reports_QuoteOrderCrossRef_ALL",
	Reports_GenerateMailingList_ALL = "Reports_GenerateMailingList_ALL",
	Reports_SalesSummaryByState_ALL = "Reports_SalesSummaryByState_ALL",
	Reports_SalesDetailByOrderRep_ALL = "Reports_SalesDetailByOrderRep_ALL",
	Reports_SalesDetailByStyleCodeRep_ALL = "Reports_SalesDetailByStyleCodeRep_ALL",
	Reports_ItemOrderActivitySummaryByMonth_ALL = "Reports_ItemOrderActivitySummaryByMonth_ALL",
	Reports_OutstandingCreditByRep_ALL = "Reports_OutstandingCreditByRep_ALL",
	Reports_PriceDeviationReport_ALL = "Reports_PriceDeviationReport_ALL",
	Reports_CustomerSales_3Year_ALL = "Reports_CustomerSales_3Year_ALL",
	Reports_SalesByMonth_ALL = "Reports_SalesByMonth_ALL",
	Reports_ShipReportForReps_ALL = "Reports_ShipReportForReps_ALL",
	Reports_SalesCVSTopStates_ALL = "Reports_SalesCVSTopStates_ALL",
	Reports_POReportByVendor_ALL = "Reports_POReportByVendor_ALL",
	Reports_AvailableStock_ALL = "Reports_AvailableStock_ALL",
	Reports_DemandHistory_ALL = "Reports_DemandHistory_ALL",
	Reports_Vendor_ALL = "Reports_Vendor_ALL",
	Reports_SuppliedBy_ALL = "Reports_SuppliedBy_ALL",
	// Shipping permissions (API-level)
	Shipping_Shipments_Create = "Shipping_Shipments_Create",
	Shipping_Shipments_View = "Shipping_Shipments_View",
	Shipping_Labels_Create = "Shipping_Labels_Create",
	Shipping_Rates_View = "Shipping_Rates_View",
	Shipping_Carriers_View = "Shipping_Carriers_View",
	// Shipper role specific permissions
	Shipper_Shipping_CreateLabel = "Shipper_Shipping_CreateLabel",
	Shipper_Shipping_ViewPickTicket = "Shipper_Shipping_ViewPickTicket",
	Shipper_Shipping_VoidLabel = "Shipper_Shipping_VoidLabel",
	Shipper_Shipping_CreateReturnLabel = "Shipper_Shipping_CreateReturnLabel",
	Shipper_Shipping_ViewLabels = "Shipper_Shipping_ViewLabels",
	Shipper_PickTicket_Update = "Shipper_PickTicket_Update",
	Shipper_PickTicket_Unship = "Shipper_PickTicket_Unship",
	Shipper_RecentActivity_View = "Shipper_RecentActivity_View",
	Shipper_EndOfDay_View = "Shipper_EndOfDay_View",
	Shipper_EndOfDay_Export = "Shipper_EndOfDay_Export",
	Shipper_Manifest_Create = "Shipper_Manifest_Create",
	Shipper_Manifest_View = "Shipper_Manifest_View",
	// Pending Approval permissions
	SalesOrders_PendingApproval_View = "SalesOrders_PendingApproval_View",
	SalesOrders_Quote_Approve = "SalesOrders_Quote_Approve",
	SalesOrders_Quote_Revert = "SalesOrders_Quote_Revert",
	SalesOrders_Quote_AdjustTaxRate = "SalesOrders_Quote_AdjustTaxRate",
	// Sample Orders permissions
	SalesOrders_SampleOrder_View = "SalesOrders_SampleOrder_View",
	SalesOrders_SampleOrder_MarkReturned = "SalesOrders_SampleOrder_MarkReturned",
}
